import type { ReactElement, RefObject } from "react";
import { useMemo, useRef, useState } from "react";

import { debounce } from "lodash";

import { colorsPalettes } from "@similarweb/styles";
import { DotsLoader, Popover, PopoverTrigger, Textfield } from "@similarweb/ui-components";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { SuperAgentButton } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentButton/SuperAgentButton";
import useCompanyAutocompleteQuery from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteQuery";
import { LowCreditsBanner } from "app/pages/sales-intelligence/modules/quota/components/LowCreditsBanner/LowCreditsBanner";
import { noTouchLowCreditsBannerConfigs } from "app/pages/sales-intelligence/modules/quota/constants/configuration";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { useLowCreditsBannerVisibility } from "app/pages/sales-intelligence/modules/quota/hooks/useLowCreditsBannerVisibility";
import { useLowQuotaBannerManagement } from "app/pages/sales-intelligence/modules/quota/hooks/useLowQuotaBannerManagement";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";
import { swSettings } from "app/common/services/swSettings";
import { isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import CompanyAutocompleteItem from "../CompanyAutocompleteItem/CompanyAutocompleteItem";
import CompanyAutocompleteUpgradeBlock from "../CompanyAutocompleteUpgradeBlock/CompanyAutocompleteUpgradeBlock";
import NoSearchResults from "../NoSearchResults/NoSearchResults";

import {
    StyledAutocompleteLowBannerWrapper,
    StyledCompanyPlaceholderText,
    StyledPopoverContent,
    StyledPopoverContentBody,
    StyledQuota,
} from "./styles";

const MAX_RESULTS_QUOTA_REACHED = 4;
const MAX_RESULTS = 8;

const CompanyAutocomplete = ({
    placeholderKey,
    autoFocus,
    renderOnEmptyQuery,
    onCompanyClick,
    onWebsiteClick,
    className,
    hideLabel = false,
    withAIButton = false,
}: {
    placeholderKey?: string;
    autoFocus?: boolean;
    renderOnEmptyQuery?: () => ReactElement | null;
    onCompanyClick: ({ key, companyName }: { key: string; companyName: string }) => void;
    onWebsiteClick: (domain: string) => void;
    className?: string;
    hideLabel?: boolean;
    withAIButton?: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [term, setTerm] = useState("");
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);

    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;
    const { data: quota, isQuotaUnlimited, isQuotaReached } = useCompanyQuota();

    const showBanner = useLowCreditsBannerVisibility({
        totalQuota: quota?.total || 0,
        remainingQuota: quota?.remaining || 0,
    });
    const { lowCreditsBannerConfigs: trialBannerConfigs } = useLowQuotaBannerManagement();
    const bannerConfigs = isTrial(swSettings) ? trialBannerConfigs : noTouchLowCreditsBannerConfigs;
    const { data, isLoading } = useCompanyAutocompleteQuery(
        term,
        isQuotaReached ? MAX_RESULTS_QUOTA_REACHED : MAX_RESULTS,
        isDataCreditsEnabled,
    );
    const debouncedSetTerm = useMemo(() => debounce(setTerm, 250), []);

    const cleanedTerm = term.toLowerCase().replace(/,/g, " ");

    const handleCompanyClick = (
        item: IAutoCompleteSiteItem,
        { key, companyName }: { key: string; companyName: string },
    ) => {
        if (isDataCreditsEnabled ? !item.isPurchased && isQuotaReached : isQuotaReached) {
            return;
        }

        setIsOpen(false);
        setTerm("");
        onCompanyClick({ key, companyName });
    };

    const handleWebsiteClick = (item: IAutoCompleteSiteItem, domain: string) => {
        if (isDataCreditsEnabled ? !item.isPurchased && isQuotaReached : isQuotaReached) {
            return;
        }

        setIsOpen(false);
        setTerm("");
        onWebsiteClick(domain);
    };

    const handleToggle = (open: boolean, event?: Event) => {
        const isWithinSearch = inputWrapperRef.current?.contains(event?.target as Node);

        if (isWithinSearch) {
            return;
        }
        setIsOpen(open);
    };

    const handleAIButtonClick = () => {
        AIAgentTrackingService.superAgent.trackSAButtonClick();
        void siNavigator.goToSuperAgentPage();
    };

    const isQuotaRenderedInInput = !isQuotaUnlimited && !isLoading && isOpen && (term || renderOnEmptyQuery);

    const renderSearchResults = () => {
        if (term) {
            if (Array.isArray(data) && data.length === 0) {
                return <NoSearchResults searchTerm={term} />;
            }
            if (Array.isArray(data)) {
                return (
                    <>
                        {data.map((item: IAutoCompleteSiteItem) => (
                            <CompanyAutocompleteItem
                                key={item.name}
                                company={item}
                                hideLabel={hideLabel}
                                onCompanyClick={(options: { key: string; companyName: string; image?: string }) =>
                                    handleCompanyClick(item, options)
                                }
                                onWebsiteClick={(domain: string) => handleWebsiteClick(item, domain)}
                                queryWords={cleanedTerm.split(" ").filter(Boolean)}
                                isLocked={isDataCreditsEnabled ? isQuotaReached && !item.isPurchased : isQuotaReached}
                            />
                        ))}
                        {!isDataCreditsEnabled && isQuotaReached ? (
                            <CompanyAutocompleteUpgradeBlock interval={quota?.interval} />
                        ) : null}
                    </>
                );
            }
            return null;
        }

        const emptyQueryResult = renderOnEmptyQuery?.();
        if (emptyQueryResult) {
            return emptyQueryResult;
        }

        return null;
    };

    const dropdownContent = isDataCreditsEnabled ? (
        <>
            <StyledPopoverContentBody>{renderSearchResults()}</StyledPopoverContentBody>
            {Array.isArray(data) && data.length !== 0 && showBanner ? (
                <StyledAutocompleteLowBannerWrapper>
                    <LowCreditsBanner {...bannerConfigs} />
                </StyledAutocompleteLowBannerWrapper>
            ) : null}
        </>
    ) : (
        renderSearchResults()
    );

    return (
        <Box
            position="relative"
            className={className}
        >
            {withAIButton ? (
                <Box
                    position="absolute"
                    top="2px"
                    right="8px"
                    zIndex={1}
                >
                    <SuperAgentButton onClick={handleAIButtonClick} />
                </Box>
            ) : null}
            <Box
                ref={inputWrapperRef}
                onClick={() => {
                    setIsOpen(true);
                }}
            >
                <Textfield
                    isFocused={isOpen}
                    onChange={debouncedSetTerm}
                    placeholder={
                        <StyledCompanyPlaceholderText>
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[300]}
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        placeholderKey || "si.pages.home.search.autocomplete.placeholder.company.text",
                                    ),
                                }}
                            />
                        </StyledCompanyPlaceholderText>
                    }
                    isLoading={isLoading}
                    loadingComponent={<DotsLoader />}
                    autoFocus={autoFocus}
                    iconName="search"
                />
                {!isDataCreditsEnabled && isQuotaRenderedInInput ? (
                    <StyledQuota>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.midnight[100]}
                        >
                            {`${quota?.used}/${quota?.total}`} {translate("si.pages.home.search.autocomplete.quota")}
                        </Text>
                    </StyledQuota>
                ) : null}
            </Box>
            <Popover
                isClickOutsideEnabled
                open={isOpen}
                onOpenChange={handleToggle}
                placement="bottom-start"
                initialFocus={-1}
            >
                <PopoverTrigger elementRef={inputWrapperRef as RefObject<HTMLElement>} />
                <StyledPopoverContent>{dropdownContent}</StyledPopoverContent>
            </Popover>
        </Box>
    );
};

export default CompanyAutocomplete;
